import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/ui';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSignatureSetup } from '../hooks/useSignatureSetup';
import {
  subscribeToDocumentSignature,
  signWithAssent,
  signWithTypedName,
  signWithPin,
  signWithBiometric,
} from '../services/signingService';
import SignatureSetup from './SignatureSetup';
import SignatureMark from './SignatureMark';

const formatDate = (input) => {
  if (!input) return '';
  const d = input.toDate ? input.toDate() : new Date(input);
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SignatureWidget = ({ documentId, documentTitle, onSigned }) => {
  const { user } = useAuth();
  const { setup, loading: setupLoading } = useSignatureSetup();
  const [signature, setSignature] = useState(null);
  const [sigLoading, setSigLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const onSignedRef = useRef(onSigned);
  onSignedRef.current = onSigned;
  const signaledRef = useRef(false);

  useEffect(() => {
    if (!user?.uid || !documentId) return;
    setSigLoading(true);
    const unsubscribe = subscribeToDocumentSignature(documentId, user.uid, (sig) => {
      setSignature(sig);
      setSigLoading(false);
      // Fires once per mount, including when the document was already signed —
      // hosts that record "signed" elsewhere rely on this to catch up.
      if (sig && !signaledRef.current) {
        signaledRef.current = true;
        onSignedRef.current?.(sig);
      }
    });
    return unsubscribe;
  }, [user?.uid, documentId]);

  const loading = setupLoading || sigLoading;

  return (
    <div className="rounded-xl border-2 border-base-300 bg-gradient-to-br from-base-100 to-base-200/30 overflow-hidden">
      <div className="px-5 py-3 bg-base-200/50 border-b border-base-300 flex items-center gap-2">
        <span className="text-primary text-lg leading-none">✒︎</span>
        <span className="text-xs uppercase tracking-[0.2em] text-base-content/60 font-semibold">
          Signature required
        </span>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-base-content/50 text-sm">
            <span className="loading loading-spinner loading-xs" />
            <span>Loading…</span>
          </div>
        ) : signature ? (
          <SignedState signature={signature} />
        ) : !setup ? (
          <SetupPrompt
            documentTitle={documentTitle}
            onStart={() => setShowSetup(true)}
          />
        ) : (
          <SignControls
            documentId={documentId}
            setup={setup}
          />
        )}
      </div>

      {showSetup && (
        <SignatureSetup
          onClose={() => setShowSetup(false)}
          onComplete={() => setShowSetup(false)}
        />
      )}
    </div>
  );
};

// ── Signed state ────────────────────────────────────────────────────────────
const SignedState = ({ signature }) => (
  <div className="space-y-3">
    <SignatureMark signature={signature} />
    <div className="text-xs text-base-content/50">
      Signed on {formatDate(signature.signedAt)} via{' '}
      {methodLabel(signature.method)}.
    </div>
  </div>
);

const methodLabel = (method) => {
  if (method === 'typed_name') return 'typed name';
  if (method === 'biometric') return 'biometric';
  if (method === 'pin') return 'PIN';
  if (method === 'assent') return 'tap to agree';
  return method;
};

// ── Setup prompt ────────────────────────────────────────────────────────────
const SetupPrompt = ({ documentTitle, onStart }) => (
  <div className="space-y-3">
    <div>
      <div className="font-semibold text-base-content">
        You haven't set up a signature yet
      </div>
      <p className="text-sm text-base-content/70 mt-1 leading-relaxed">
        Before you can sign{documentTitle ? ` "${documentTitle}"` : ' this'},
        you'll need to set up a signature. It only takes a minute, and it's
        yours from then on.
      </p>
    </div>
    <div>
      <Button variant="primary" onClick={onStart}>
        Set up my signature
      </Button>
    </div>
  </div>
);

// ── Active sign controls ────────────────────────────────────────────────────
const SignControls = ({ documentId, setup }) => {
  if (setup.method === 'assent') {
    return <AssentSign documentId={documentId} setup={setup} />;
  }
  if (setup.method === 'typed_name') {
    return <TypedNameSign documentId={documentId} setup={setup} />;
  }
  if (setup.method === 'pin') {
    return <PinSign documentId={documentId} setup={setup} />;
  }
  if (setup.method === 'biometric') {
    return <BiometricSign documentId={documentId} setup={setup} />;
  }
  return null;
};

const AssentSign = ({ documentId, setup }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signWithAssent({ documentId });
    } catch (e) {
      setError(e.message || 'Could not sign.');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-base-content/70 leading-relaxed">
        Tapping Sign confirms your agreement
        {setup.legalName ? (
          <> as <span className="font-serif font-medium">{setup.legalName}</span></>
        ) : null}.
      </p>
      {error && <div className="text-sm text-error">{error}</div>}
      <div className="flex justify-end">
        <Button variant="primary" loading={submitting} onClick={submit}>
          Sign
        </Button>
      </div>
    </div>
  );
};

const TypedNameSign = ({ documentId, setup }) => {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const matches = typed.trim().toLowerCase() === (setup.legalName || '').toLowerCase();

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signWithTypedName({ documentId, typedName: typed.trim() });
    } catch (e) {
      setError(e.message || 'Could not sign.');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-base-content/70">
        Type your full legal name to sign:
      </div>
      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={setup.legalName}
        autoComplete="off"
        className="input input-bordered w-full font-serif text-lg h-13"
      />
      {error && <div className="text-sm text-error">{error}</div>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          loading={submitting}
          disabled={!matches}
          onClick={submit}
        >
          Sign
        </Button>
      </div>
    </div>
  );
};

const PinSign = ({ documentId, setup }) => {
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (pin.length !== 4) {
      setError('Enter your 4-digit PIN.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await signWithPin({ documentId, pin });
    } catch (e) {
      setError(e.message || 'That PIN didn\'t match.');
      setPin('');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-base-content/70">
        Enter your PIN to sign as <span className="font-serif font-medium">{setup.legalName}</span>.
      </div>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder="····"
        maxLength={4}
        className="input input-bordered w-full font-mono tracking-[0.5em] text-center text-xl h-14"
      />
      {error && <div className="text-sm text-error">{error}</div>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          loading={submitting}
          disabled={pin.length !== 4}
          onClick={submit}
        >
          Sign
        </Button>
      </div>
    </div>
  );
};

const BiometricSign = ({ documentId, setup }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signWithBiometric({ documentId });
    } catch (e) {
      const msg = e?.message || '';
      if (/cancel|abort|NotAllowed/i.test(msg)) {
        setError('Cancelled. Try again when ready.');
      } else {
        setError(msg || 'Could not sign.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-base-content/70">
        Confirm with your fingerprint or face to sign as{' '}
        <span className="font-serif font-medium">{setup.legalName}</span>.
      </div>
      {error && <div className="text-sm text-error">{error}</div>}
      <div className="flex justify-end">
        <Button variant="primary" loading={submitting} onClick={submit}>
          <span className="mr-2">☉</span> Sign with biometric
        </Button>
      </div>
    </div>
  );
};

export default SignatureWidget;
